import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { ShoppingCart, User, LogOut, LayoutDashboard, Menu, X, ArrowLeft, Home, Search, Package, BookOpen, Users, ChevronDown, Gamepad2, Store, Sparkles, Heart, GraduationCap } from 'lucide-react';
import api from '../utils/api';
import NotificationBell from './NotificationBell';
import LanguageSelector from './LanguageSelector';
import { InlineVisitorCounter } from './VisitorCounter';

const Navbar = () => {
  const { user, logout, isAdmin } = useAuth();
  const { cartCount, fetchCart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ products: [], courses: [], users: [] });
  const [searching, setSearching] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [moreSearch, setMoreSearch] = useState('');
  const searchRef = useRef(null);
  const moreMenuRef = useRef(null);

  // Auto hide on scroll-down, show on scroll-up
  const [navVisible, setNavVisible] = useState(true);
  const lastScrollY = useRef(0);
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      // Always show near top
      if (y < 80) { setNavVisible(true); lastScrollY.current = y; return; }
      // Scrolling down → hide
      if (y > lastScrollY.current + 8) setNavVisible(false);
      // Scrolling up → show
      else if (y < lastScrollY.current - 8) setNavVisible(true);
      lastScrollY.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
    setShowSearch(false);
    setSearchQuery('');
    setShowMoreMenu(false);
  }, [location.pathname]);

  // Close mobile menu when scrolling
  useEffect(() => {
    const handleScroll = () => {
      if (mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [mobileMenuOpen]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Check if click is outside the navbar
      const navbar = document.querySelector('nav');
      if (navbar && !navbar.contains(e.target) && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSearch(false);
      }
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search function
  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults({ products: [], courses: [], users: [] });
      return;
    }
    
    setSearching(true);
    try {
      const [productsRes, coursesRes] = await Promise.all([
        api.get(`/products?search=${query}`),
        api.get(`/courses?search=${query}`)
      ]);
      
      let users = [];
      if (isAdmin && query.length >= 3) {
        try {
          const usersRes = await api.get(`/admin/users?search=${query}&limit=5`);
          users = usersRes.data.users || [];
        } catch (e) {
          // Silently fail for non-admin users
        }
      }
      
      setSearchResults({
        products: (productsRes.data || []).slice(0, 5),
        courses: (coursesRes.data || []).slice(0, 5),
        users: users.slice(0, 5)
      });
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleSearchSelect = (path) => {
    setShowSearch(false);
    setSearchQuery('');
    navigate(path);
  };

  useEffect(() => {
    if (user) {
      fetchCart();
    }
    fetchSettings();
  }, [user]);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const handleLogout = () => {
    setMobileMenuOpen(false);
    logout();
    navigate('/');
  };

  // Close menu when clicking a link
  const closeMenu = () => {
    setMobileMenuOpen(false);
  };

  // Check if we can go back
  const canGoBack = location.pathname !== '/' && location.pathname !== '/login' && location.pathname !== '/register';

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 bg-background border-b border-white/10 transition-transform duration-300 ${navVisible ? 'translate-y-0' : '-translate-y-full'}`}
      data-testid="navbar-root"
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Back Button + Logo */}
          <div className="flex items-center gap-3">
            {/* Back Button - Mobile */}
            {canGoBack && (
              <button
                onClick={handleBack}
                className="md:hidden p-2 hover:bg-white/10 rounded-lg transition-colors"
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3" data-testid="navbar-logo" onClick={closeMenu}>
              <img src="/logo.png" alt="Logo" className="h-10 w-auto" />
              <div className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary via-primary to-accent hidden sm:block">
                {settings?.store_name || 'ANANT WEB PORTAL'}
              </div>
            </Link>
            
            {/* Visitor Counter - Next to Logo */}
            <div className="block">
              <InlineVisitorCounter />
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {/* Search Button */}
            <div className="relative" ref={searchRef}>
              <button
                onClick={() => setShowSearch(!showSearch)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                data-testid="search-button"
              >
                <Search className="w-5 h-5" />
              </button>
              
              {/* Search Dropdown */}
              {showSearch && (
                <div className="absolute right-0 top-full mt-2 w-96 bg-card border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50">
                  <div className="p-3 border-b border-white/10">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => handleSearch(e.target.value)}
                        placeholder="Search products, courses..."
                        className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm"
                        autoFocus
                        data-testid="search-input"
                      />
                    </div>
                  </div>
                  
                  <div className="max-h-96 overflow-y-auto">
                    {searching && (
                      <div className="p-4 text-center text-muted-foreground">
                        <div className="animate-spin w-5 h-5 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
                      </div>
                    )}
                    
                    {!searching && searchQuery.length >= 2 && (
                      <>
                        {/* Products */}
                        {searchResults.products.length > 0 && (
                          <div className="p-2">
                            <p className="text-xs text-muted-foreground px-2 mb-1">Products</p>
                            {searchResults.products.map(product => (
                              <button
                                key={product.id}
                                onClick={() => handleSearchSelect(`/products/${product.id}`)}
                                className="w-full flex items-center gap-3 p-2 hover:bg-white/5 rounded-lg text-left"
                              >
                                <Package className="w-4 h-4 text-primary" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">{product.name}</p>
                                  <p className="text-xs text-muted-foreground">₹{product.price}</p>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                        
                        {/* Courses */}
                        {searchResults.courses.length > 0 && (
                          <div className="p-2 border-t border-white/5">
                            <p className="text-xs text-muted-foreground px-2 mb-1">Courses</p>
                            {searchResults.courses.map(course => (
                              <button
                                key={course.id}
                                onClick={() => handleSearchSelect(`/courses/${course.id}`)}
                                className="w-full flex items-center gap-3 p-2 hover:bg-white/5 rounded-lg text-left"
                              >
                                <BookOpen className="w-4 h-4 text-secondary" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">{course.name}</p>
                                  <p className="text-xs text-muted-foreground">₹{course.price}</p>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                        
                        {/* Users (Admin only) */}
                        {isAdmin && searchResults.users.length > 0 && (
                          <div className="p-2 border-t border-white/5">
                            <p className="text-xs text-muted-foreground px-2 mb-1">Users</p>
                            {searchResults.users.map(u => (
                              <button
                                key={u.id}
                                onClick={() => handleSearchSelect(`/admin/users?search=${u.username}`)}
                                className="w-full flex items-center gap-3 p-2 hover:bg-white/5 rounded-lg text-left"
                              >
                                <Users className="w-4 h-4 text-accent" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">@{u.username}</p>
                                  <p className="text-xs text-muted-foreground">{u.email}</p>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                        
                        {/* No Results */}
                        {searchResults.products.length === 0 && 
                         searchResults.courses.length === 0 && 
                         searchResults.users.length === 0 && (
                          <div className="p-4 text-center text-muted-foreground text-sm">
                            No results found for &quot;{searchQuery}&quot;
                          </div>
                        )}
                      </>
                    )}
                    
                    {!searching && searchQuery.length < 2 && searchQuery.length > 0 && (
                      <div className="p-4 text-center text-muted-foreground text-sm">
                        Type at least 2 characters to search
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <Link to="/products" className="text-foreground hover:text-primary transition-colors whitespace-nowrap" data-testid="nav-products">
              Products
            </Link>
            <Link to="/courses" className="text-foreground hover:text-primary transition-colors whitespace-nowrap" data-testid="nav-courses">
              Courses
            </Link>
            <Link to="/sankat-mochan" className="text-foreground hover:text-primary transition-colors flex items-center gap-1 whitespace-nowrap" data-testid="nav-samadhan">
              <Heart className="w-4 h-4 text-green-500" />
              <span>Samadhan</span>
            </Link>
            <Link to="/algotrading" className="text-foreground hover:text-primary transition-colors flex items-center gap-1 whitespace-nowrap" data-testid="nav-trading-terminal">
              <Sparkles className="w-4 h-4 text-pink-400" />
              <span>Trading</span>
            </Link>
            <Link to="/education" className="text-foreground hover:text-primary transition-colors flex items-center gap-1 whitespace-nowrap" data-testid="nav-learn-trading">
              <GraduationCap className="w-4 h-4 text-indigo-400" />
              <span>Learn Trading</span>
            </Link>
            {user?.role === 'admin' && (
              <Link
                to="/anant-admin"
                className="text-foreground hover:text-primary transition-colors flex items-center gap-1 whitespace-nowrap"
                data-testid="nav-academy-admin"
              >
                <Sparkles className="w-4 h-4 text-amber-400" />
                <span>Academy Admin</span>
                <span className="text-[10px] bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded-full font-bold">ALL</span>
              </Link>
            )}
            
            {/* More Menu Dropdown */}
            <div className="relative" ref={moreMenuRef}>
              <button
                onClick={() => setShowMoreMenu(!showMoreMenu)}
                className="text-foreground hover:text-primary transition-colors flex items-center gap-1 whitespace-nowrap"
                data-testid="nav-more-menu"
              >
                <span>More</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showMoreMenu ? 'rotate-180' : ''}`} />
              </button>
              
              {showMoreMenu && (
                <div className="absolute right-0 top-full mt-2 w-72 bg-card border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50">
                  <div className="p-2 border-b border-white/10 sticky top-0 bg-card z-10">
                    <input value={moreSearch} onChange={(e) => setMoreSearch(e.target.value)}
                           placeholder="🔍 Search 30+ modules…" data-testid="nav-more-search"
                           className="w-full px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-xs text-white" />
                    <div className="flex gap-1 mt-2 overflow-x-auto text-[9px]">
                      {['All', 'Marketplace', 'Wallet & Rewards', 'Trading', 'Business Tools', 'Communication', 'Learning'].map((g) => (
                        <button key={g} onClick={() => setMoreSearch(g === 'All' ? '' : g)}
                                className={`shrink-0 px-2 py-0.5 rounded-full font-black uppercase ${moreSearch === (g === 'All' ? '' : g) ? 'bg-primary text-white' : 'bg-slate-700 text-slate-300'}`}>
                          {g}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="py-2 more-menu-scroll max-h-[70vh] overflow-y-auto"
                       style={{ '--more-q': moreSearch }}
                       onKeyUp={(e) => {
                         const q = moreSearch.toLowerCase().trim();
                         const items = e.currentTarget.querySelectorAll('[data-more-item]');
                         items.forEach((el) => {
                           const t = (el.textContent || '').toLowerCase();
                           el.style.display = !q || t.includes(q) ? '' : 'none';
                         });
                       }}
                       ref={(el) => {
                         if (!el) return;
                         const q = moreSearch.toLowerCase().trim();
                         el.querySelectorAll('[data-more-item]').forEach((it) => {
                           const t = (it.textContent || '').toLowerCase();
                           it.style.display = !q || t.includes(q) ? '' : 'none';
                         });
                         // Hide group headers when their following items are all hidden
                         el.querySelectorAll('[data-more-group]').forEach((gh) => {
                           let sib = gh.nextElementSibling;
                           let anyVisible = false;
                           while (sib && !sib.hasAttribute('data-more-group')) {
                             if (sib.style.display !== 'none' && getComputedStyle(sib).display !== 'none') { anyVisible = true; break; }
                             sib = sib.nextElementSibling;
                           }
                           gh.style.display = anyVisible ? '' : 'none';
                         });
                       }}>
                    <div data-more-group="Marketplace" className="px-4 pt-2 pb-1 text-[9px] font-black uppercase tracking-widest text-purple-300 marketplace">🛒 Marketplace</div>
                    <Link 
                      to="/shop-templates" 
                      onClick={() => setShowMoreMenu(false)}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors"
                      data-more-item="marketplace"
                    >
                      <Store className="w-4 h-4 text-blue-400" />
                      <span>Shop Templates</span>
                      <span className="ml-auto text-xs bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded-full">NEW</span>
                    </Link>
                    <Link 
                      to="/games" 
                      onClick={() => setShowMoreMenu(false)}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors"
                    >
                      <Gamepad2 className="w-4 h-4 text-primary" />
                      <span>Games</span>
                      <span className="ml-auto text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded-full">NEW</span>
                    </Link>
                    <Link 
                      to="/marketing" 
                      onClick={() => setShowMoreMenu(false)}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors"
                    >
                      <Sparkles className="w-4 h-4 text-purple-400" />
                      <span>Marketing AI</span>
                      <span className="ml-auto text-xs bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded-full">NEW</span>
                    </Link>
                    <div className="border-t border-white/10 my-1"></div>
                    <div data-more-group="Business Tools" className="px-4 pt-2 pb-1 text-[9px] font-black uppercase tracking-widest text-cyan-300 business-tools">🧰 Business Tools · Trading · Wallet & Rewards</div>
                    <Link 
                      to="/p2p-deals" 
                      onClick={() => setShowMoreMenu(false)}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors"
                    >
                      <Users className="w-4 h-4 text-green-400" />
                      <span>P2P Deals</span>
                    </Link>
                    <Link 
                      to="/auctions" 
                      onClick={() => setShowMoreMenu(false)}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors"
                    >
                      <Package className="w-4 h-4 text-yellow-400" />
                      <span>Auctions</span>
                    </Link>
                    <Link 
                      to="/avx" 
                      onClick={() => setShowMoreMenu(false)}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors"
                      data-testid="nav-avx"
                    >
                      <Sparkles className="w-4 h-4 text-yellow-400" />
                      <span>AVX Exchange</span>
                      <span className="ml-auto text-xs bg-yellow-500/20 text-yellow-400 px-1.5 py-0.5 rounded-full">FANTASY</span>
                    </Link>
                    <Link 
                      to="/tsb" 
                      onClick={() => setShowMoreMenu(false)}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors"
                      data-testid="nav-tsb"
                    >
                      <Sparkles className="w-4 h-4 text-amber-400" />
                      <span>TSB Rewards</span>
                      <span className="ml-auto text-xs bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded-full">50/50</span>
                    </Link>
                    <Link 
                      to="/vendor" 
                      onClick={() => setShowMoreMenu(false)}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors"
                      data-testid="nav-vendor"
                    >
                      <Sparkles className="w-4 h-4 text-orange-400" />
                      <span>Vendor Portal</span>
                      <span className="ml-auto text-xs bg-orange-500/20 text-orange-300 px-1.5 py-0.5 rounded-full">SELL</span>
                    </Link>
                    <Link 
                      to="/explorer" 
                      onClick={() => setShowMoreMenu(false)}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors"
                      data-testid="nav-explorer"
                    >
                      <Sparkles className="w-4 h-4 text-blue-400" />
                      <span>TSB Explorer</span>
                      <span className="ml-auto text-xs bg-blue-500/20 text-blue-300 px-1.5 py-0.5 rounded-full">PUBLIC</span>
                    </Link>
                    <Link 
                      to="/algotrading" 
                      onClick={() => setShowMoreMenu(false)}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors"
                      data-testid="nav-algotrading"
                    >
                      <Sparkles className="w-4 h-4 text-pink-400" />
                      <span>Anant Smart Trading</span>
                      <span className="ml-auto text-xs bg-pink-500/20 text-pink-300 px-1.5 py-0.5 rounded-full">SaaS</span>
                    </Link>
                    <Link
                      to="/vyapaar-master"
                      onClick={() => setShowMoreMenu(false)}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors"
                      data-testid="nav-vyapaar-master"
                    >
                      <Sparkles className="w-4 h-4 text-emerald-400" />
                      <span>Anant Vyapaar Master</span>
                      <span className="ml-auto text-xs bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 rounded-full">WA</span>
                    </Link>
                    <Link
                      to="/flyer-studio"
                      onClick={() => setShowMoreMenu(false)}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors"
                      data-testid="nav-flyer-studio"
                    >
                      <Sparkles className="w-4 h-4 text-orange-400" />
                      <span>Anant Smart Flyer</span>
                      <span className="ml-auto text-xs bg-orange-500/20 text-orange-300 px-1.5 py-0.5 rounded-full">NEW</span>
                    </Link>
                    <Link
                      to="/anant-tarot"
                      onClick={() => setShowMoreMenu(false)}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors"
                      data-testid="nav-anant-tarot"
                    >
                      <Sparkles className="w-4 h-4 text-fuchsia-400" />
                      <span>Anant Inner Self</span>
                      <span className="ml-auto text-xs bg-fuchsia-500/20 text-fuchsia-300 px-1.5 py-0.5 rounded-full">TAROT</span>
                    </Link>
                    <Link
                      to="/education"
                      onClick={() => setShowMoreMenu(false)}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors"
                      data-testid="nav-education"
                    >
                      <GraduationCap className="w-4 h-4 text-indigo-400" />
                      <span>Education Hub</span>
                      <span className="ml-auto text-xs bg-indigo-500/20 text-indigo-300 px-1.5 py-0.5 rounded-full">AI TUTOR</span>
                    </Link>
                    <Link
                      to="/smart-card/manage"
                      onClick={() => setShowMoreMenu(false)}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors"
                      data-testid="nav-smart-card"
                    >
                      <Sparkles className="w-4 h-4 text-amber-400" />
                      <span>Smart Business Cards</span>
                      <span className="ml-auto text-xs bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded-full">23 TEMPLATES</span>
                    </Link>
                    <Link
                      to="/academy"
                      onClick={() => setShowMoreMenu(false)}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors"
                      data-testid="nav-academy"
                    >
                      <GraduationCap className="w-4 h-4 text-emerald-400" />
                      <span>Freedom Academy</span>
                      <span className="ml-auto text-xs bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 rounded-full">NEW</span>
                    </Link>
                    {user?.role === 'admin' && (
                      <Link 
                        to="/admin/displayengine" 
                        onClick={() => setShowMoreMenu(false)}
                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors"
                        data-testid="nav-displayengine-admin"
                      >
                        <Sparkles className="w-4 h-4 text-amber-400" />
                        <span>Display Engine</span>
                        <span className="ml-auto text-xs bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded-full">ADMIN</span>
                      </Link>
                    )}
                    {user?.role === 'admin' && (
                      <Link 
                        to="/admin/algotrading" 
                        onClick={() => setShowMoreMenu(false)}
                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors"
                        data-testid="nav-algotrading-admin"
                      >
                        <Sparkles className="w-4 h-4 text-violet-400" />
                        <span>Anant Smart Trading Admin</span>
                        <span className="ml-auto text-xs bg-violet-500/20 text-violet-300 px-1.5 py-0.5 rounded-full">ADMIN</span>
                      </Link>
                    )}
                    {user?.role === 'admin' && (
                      <Link 
                        to="/anant-admin" 
                        onClick={() => setShowMoreMenu(false)}
                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors border-t border-amber-500/20 mt-1 pt-2"
                        data-testid="nav-anant-admin"
                      >
                        <Sparkles className="w-4 h-4 text-amber-400" />
                        <span>🎛️ Master Admin Console</span>
                        <span className="ml-auto text-xs bg-amber-500/30 text-amber-200 px-1.5 py-0.5 rounded-full font-bold">ALL-IN-ONE</span>
                      </Link>
                    )}
                    <Link 
                      to="/free-tools" 
                      onClick={() => setShowMoreMenu(false)}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors"
                    >
                      <BookOpen className="w-4 h-4 text-cyan-400" />
                      <span>Free Tools</span>
                    </Link>
                  </div>
                </div>
              )}
            </div>
            
            {/* Language Selector */}
            <LanguageSelector />
            
            {user && (
              <>
                {/* Notification Bell */}
                <NotificationBell />
                
                <Link to="/cart" className="relative text-foreground hover:text-primary transition-colors" data-testid="nav-cart">
                  <ShoppingCart className="w-5 h-5" />
                  {cartCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold" data-testid="cart-count">
                      {cartCount}
                    </span>
                  )}
                </Link>
                
                {isAdmin && (
                  <Link to="/admin" className="text-foreground hover:text-primary transition-colors flex items-center gap-2" data-testid="nav-admin">
                    <LayoutDashboard className="w-5 h-5" />
                    Admin
                  </Link>
                )}
                
                <Link to="/dashboard" className="text-foreground hover:text-primary transition-colors" data-testid="nav-dashboard">
                  Dashboard
                </Link>
                
                <Link to="/profile" className="text-foreground hover:text-primary transition-colors" data-testid="nav-profile">
                  <User className="w-5 h-5" />
                </Link>
                
                <button
                  onClick={handleLogout}
                  className="text-foreground hover:text-primary transition-colors"
                  data-testid="logout-button"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </>
            )}
            
            {!user && (
              <>
                <Link to="/login" className="text-foreground hover:text-primary transition-colors" data-testid="nav-login">
                  Login
                </Link>
                <Link
                  to="/register"
                  className="px-6 py-2 bg-primary text-primary-foreground rounded-full font-semibold hover:bg-primary/90 transition-all glow-gold"
                  data-testid="nav-register"
                >
                  Register
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-foreground p-2 hover:bg-white/10 rounded-lg transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu - Slide down animation */}
        <div 
          className={`md:hidden transition-all duration-300 ease-in-out relative z-50 bg-background ${
            mobileMenuOpen ? 'max-h-[calc(100vh-80px)] opacity-100 mt-4 pb-24 overflow-y-auto' : 'max-h-0 opacity-0 overflow-hidden pointer-events-none'
          }`}
          data-testid="mobile-menu"
        >
          <div className="space-y-1">
            {/* Mobile Search */}
            <div className="px-4 py-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Search..."
                  className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm"
                  data-testid="mobile-search-input"
                />
              </div>
              
              {/* Mobile Search Results */}
              {searchQuery.length >= 2 && (
                <div className="mt-2 bg-white/5 rounded-lg overflow-hidden">
                  {searching && (
                    <div className="p-3 text-center">
                      <div className="animate-spin w-4 h-4 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
                    </div>
                  )}
                  
                  {!searching && searchResults.products.slice(0, 3).map(product => (
                    <button
                      key={product.id}
                      onClick={() => { closeMenu(); handleSearchSelect(`/products/${product.id}`); }}
                      className="w-full flex items-center gap-2 p-2 hover:bg-white/5 text-left text-sm"
                    >
                      <Package className="w-4 h-4 text-primary" />
                      <span className="truncate">{product.name}</span>
                    </button>
                  ))}
                  
                  {!searching && searchResults.courses.slice(0, 3).map(course => (
                    <button
                      key={course.id}
                      onClick={() => { closeMenu(); handleSearchSelect(`/courses/${course.id}`); }}
                      className="w-full flex items-center gap-2 p-2 hover:bg-white/5 text-left text-sm"
                    >
                      <BookOpen className="w-4 h-4 text-secondary" />
                      <span className="truncate">{course.name}</span>
                    </button>
                  ))}
                  
                  {!searching && searchResults.products.length === 0 && searchResults.courses.length === 0 && (
                    <p className="p-2 text-center text-muted-foreground text-xs">No results</p>
                  )}
                </div>
              )}
            </div>
            
            <Link 
              to="/" 
              onClick={closeMenu}
              className="flex items-center gap-3 px-4 py-3 text-foreground hover:text-primary hover:bg-white/5 rounded-lg transition-all"
            >
              <Home className="w-5 h-5" />
              Home
            </Link>
            {user && (
              <Link 
                to="/dashboard" 
                onClick={closeMenu}
                className="flex items-center gap-3 px-4 py-3 text-foreground hover:text-primary hover:bg-white/5 rounded-lg transition-all"
                data-testid="mobile-nav-dashboard-top"
              >
                <LayoutDashboard className="w-5 h-5 text-primary" />
                Dashboard
              </Link>
            )}
            <Link 
              to="/products" 
              onClick={closeMenu}
              className="flex items-center gap-3 px-4 py-3 text-foreground hover:text-primary hover:bg-white/5 rounded-lg transition-all"
            >
              Products
            </Link>
            <Link 
              to="/courses" 
              onClick={closeMenu}
              className="flex items-center gap-3 px-4 py-3 text-foreground hover:text-primary hover:bg-white/5 rounded-lg transition-all"
            >
              Courses
            </Link>
            <Link 
              to="/shop-templates" 
              onClick={closeMenu}
              className="flex items-center gap-3 px-4 py-3 text-foreground hover:text-primary hover:bg-white/5 rounded-lg transition-all"
              data-testid="mobile-nav-shop-templates"
            >
              <span>🛒</span>
              <span>Shop Templates</span>
              <span className="text-xs bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded-full ml-auto">NEW</span>
            </Link>
            <Link 
              to="/games" 
              onClick={closeMenu}
              className="flex items-center gap-3 px-4 py-3 text-foreground hover:text-primary hover:bg-white/5 rounded-lg transition-all"
              data-testid="mobile-nav-games"
            >
              <span>🎮</span>
              <span>Games</span>
              <span className="text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded-full ml-auto">NEW</span>
            </Link>
            <Link 
              to="/marketing" 
              onClick={closeMenu}
              className="flex items-center gap-3 px-4 py-3 text-foreground hover:text-primary hover:bg-white/5 rounded-lg transition-all"
              data-testid="mobile-nav-marketing"
            >
              <span>📊</span>
              <span>Marketing AI</span>
              <span className="text-xs bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded-full ml-auto">NEW</span>
            </Link>
            <Link 
              to="/sankat-mochan" 
              onClick={closeMenu}
              className="flex items-center gap-3 px-4 py-3 text-foreground hover:text-primary hover:bg-white/5 rounded-lg transition-all"
              data-testid="mobile-nav-samadhan"
            >
              <span>🙏</span>
              <span>Samadhan</span>
              <span className="text-xs bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded-full ml-auto">NEW</span>
            </Link>
            <Link
              to="/vyapaar-master"
              onClick={closeMenu}
              className="flex items-center gap-3 px-4 py-3 text-foreground hover:text-primary hover:bg-white/5 rounded-lg transition-all"
              data-testid="mobile-nav-vyapaar-master"
            >
              <span>📱</span>
              <span>Vyapaar Master</span>
              <span className="text-xs bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded-full ml-auto">WA</span>
            </Link>
            <Link
              to="/flyer-studio"
              onClick={closeMenu}
              className="flex items-center gap-3 px-4 py-3 text-foreground hover:text-primary hover:bg-white/5 rounded-lg transition-all"
              data-testid="mobile-nav-flyer-studio"
            >
              <span>🎨</span>
              <span>Anant Smart Flyer</span>
              <span className="text-xs bg-orange-500/20 text-orange-400 px-1.5 py-0.5 rounded-full ml-auto">NEW</span>
            </Link>
            <Link 
              to="/algotrading" 
              onClick={closeMenu}
              className="flex items-center gap-3 px-4 py-3 text-foreground hover:text-primary hover:bg-white/5 rounded-lg transition-all"
              data-testid="mobile-nav-algotrading"
            >
              <Sparkles className="w-5 h-5 text-pink-400" />
              <span>Anant Smart Trading</span>
              <span className="text-xs bg-pink-500/20 text-pink-300 px-1.5 py-0.5 rounded-full ml-auto">SaaS</span>
            </Link>
            <Link
              to="/anant-tarot"
              onClick={closeMenu}
              className="flex items-center gap-3 px-4 py-3 text-foreground hover:text-primary hover:bg-white/5 rounded-lg transition-all"
              data-testid="mobile-nav-anant-tarot"
            >
              <Sparkles className="w-5 h-5 text-fuchsia-400" />
              <span>Anant Inner Self</span>
              <span className="text-xs bg-fuchsia-500/20 text-fuchsia-300 px-1.5 py-0.5 rounded-full ml-auto">TAROT</span>
            </Link>
            <Link
              to="/education"
              onClick={closeMenu}
              className="flex items-center gap-3 px-4 py-3 text-foreground hover:text-primary hover:bg-white/5 rounded-lg transition-all"
              data-testid="mobile-nav-education"
            >
              <GraduationCap className="w-5 h-5 text-indigo-400" />
              <span>Education Hub</span>
              <span className="text-xs bg-indigo-500/20 text-indigo-300 px-1.5 py-0.5 rounded-full ml-auto">AI TUTOR</span>
            </Link>
            <Link
              to="/academy"
              onClick={closeMenu}
              className="flex items-center gap-3 px-4 py-3 text-foreground hover:text-primary hover:bg-white/5 rounded-lg transition-all"
              data-testid="mobile-nav-academy"
            >
              <GraduationCap className="w-5 h-5 text-emerald-400" />
              <span>Freedom Academy</span>
              <span className="text-xs bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 rounded-full ml-auto">NEW</span>
            </Link>
            <Link
              to="/smart-card/manage"
              onClick={closeMenu}
              className="flex items-center gap-3 px-4 py-3 text-foreground hover:text-primary hover:bg-white/5 rounded-lg transition-all"
              data-testid="mobile-nav-smart-card"
            >
              <Sparkles className="w-5 h-5 text-amber-400" />
              <span>Smart Business Cards</span>
              <span className="text-xs bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded-full ml-auto">23 TPL</span>
            </Link>
            <Link
              to="/sankat-mochan"
              onClick={closeMenu}
              className="flex items-center gap-3 px-4 py-3 text-foreground hover:text-primary hover:bg-white/5 rounded-lg transition-all"
              data-testid="mobile-nav-samadhan-2"
            >
              <Heart className="w-5 h-5 text-green-500" />
              <span>Samadhan Service</span>
            </Link>
            <Link
              to="/p2p-deals"
              onClick={closeMenu}
              className="flex items-center gap-3 px-4 py-3 text-foreground hover:text-primary hover:bg-white/5 rounded-lg transition-all"
              data-testid="mobile-nav-p2p"
            >
              <Users className="w-5 h-5 text-green-400" />
              <span>P2P Deals</span>
            </Link>
            <Link
              to="/auctions"
              onClick={closeMenu}
              className="flex items-center gap-3 px-4 py-3 text-foreground hover:text-primary hover:bg-white/5 rounded-lg transition-all"
              data-testid="mobile-nav-auctions"
            >
              <Package className="w-5 h-5 text-yellow-400" />
              <span>Auctions</span>
            </Link>
            <Link
              to="/avx"
              onClick={closeMenu}
              className="flex items-center gap-3 px-4 py-3 text-foreground hover:text-primary hover:bg-white/5 rounded-lg transition-all"
              data-testid="mobile-nav-avx"
            >
              <Sparkles className="w-5 h-5 text-yellow-400" />
              <span>AVX Exchange</span>
              <span className="text-xs bg-yellow-500/20 text-yellow-400 px-1.5 py-0.5 rounded-full ml-auto">FANTASY</span>
            </Link>
            <Link
              to="/tsb"
              onClick={closeMenu}
              className="flex items-center gap-3 px-4 py-3 text-foreground hover:text-primary hover:bg-white/5 rounded-lg transition-all"
              data-testid="mobile-nav-tsb"
            >
              <Sparkles className="w-5 h-5 text-amber-400" />
              <span>TSB Rewards</span>
              <span className="text-xs bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded-full ml-auto">50/50</span>
            </Link>
            <Link
              to="/vendor"
              onClick={closeMenu}
              className="flex items-center gap-3 px-4 py-3 text-foreground hover:text-primary hover:bg-white/5 rounded-lg transition-all"
              data-testid="mobile-nav-vendor"
            >
              <Sparkles className="w-5 h-5 text-orange-400" />
              <span>Vendor Portal</span>
              <span className="text-xs bg-orange-500/20 text-orange-300 px-1.5 py-0.5 rounded-full ml-auto">SELL</span>
            </Link>
            <Link
              to="/free-tools"
              onClick={closeMenu}
              className="flex items-center gap-3 px-4 py-3 text-foreground hover:text-primary hover:bg-white/5 rounded-lg transition-all"
              data-testid="mobile-nav-free-tools"
            >
              <BookOpen className="w-5 h-5 text-cyan-400" />
              <span>Free Tools</span>
            </Link>
            {isAdmin && (
              <Link
                to="/anant-admin"
                onClick={closeMenu}
                className="flex items-center gap-3 px-4 py-3 text-foreground hover:text-primary hover:bg-white/5 rounded-lg transition-all border-t border-amber-500/20 mt-1 pt-3"
                data-testid="mobile-nav-anant-admin"
              >
                <Sparkles className="w-5 h-5 text-amber-400" />
                <span>🎛️ Master Admin Console</span>
                <span className="text-xs bg-amber-500/30 text-amber-200 px-1.5 py-0.5 rounded-full ml-auto font-bold">ALL</span>
              </Link>
            )}
            {isAdmin && (
              <Link 
                to="/admin/displayengine" 
                onClick={closeMenu}
                className="flex items-center gap-3 px-4 py-3 text-foreground hover:text-primary hover:bg-white/5 rounded-lg transition-all"
                data-testid="mobile-nav-displayengine-admin"
              >
                <Sparkles className="w-5 h-5 text-amber-400" />
                <span>Display Engine</span>
                <span className="text-xs bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded-full ml-auto">ADMIN</span>
              </Link>
            )}
            {isAdmin && (
              <Link 
                to="/admin/algotrading" 
                onClick={closeMenu}
                className="flex items-center gap-3 px-4 py-3 text-foreground hover:text-primary hover:bg-white/5 rounded-lg transition-all"
                data-testid="mobile-nav-algotrading-admin"
              >
                <Sparkles className="w-5 h-5 text-violet-400" />
                <span>Anant Smart Trading Admin</span>
                <span className="text-xs bg-violet-500/20 text-violet-300 px-1.5 py-0.5 rounded-full ml-auto">ADMIN</span>
              </Link>
            )}
            
            {user && (
              <>
                <Link 
                  to="/cart" 
                  onClick={closeMenu}
                  className="flex items-center gap-3 px-4 py-3 text-foreground hover:text-primary hover:bg-white/5 rounded-lg transition-all"
                >
                  <ShoppingCart className="w-5 h-5" />
                  Cart {cartCount > 0 && `(${cartCount})`}
                </Link>
                {isAdmin && (
                  <Link 
                    to="/admin" 
                    onClick={closeMenu}
                    className="flex items-center gap-3 px-4 py-3 text-foreground hover:text-primary hover:bg-white/5 rounded-lg transition-all"
                  >
                    <LayoutDashboard className="w-5 h-5" />
                    Admin Panel
                  </Link>
                )}
                <Link 
                  to="/profile" 
                  onClick={closeMenu}
                  className="flex items-center gap-3 px-4 py-3 text-foreground hover:text-primary hover:bg-white/5 rounded-lg transition-all"
                >
                  <User className="w-5 h-5" />
                  Profile
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 px-4 py-3 w-full text-left text-foreground hover:text-primary hover:bg-white/5 rounded-lg transition-all"
                >
                  <LogOut className="w-5 h-5" />
                  Logout
                </button>
              </>
            )}
            
            {!user && (
              <>
                <Link 
                  to="/login" 
                  onClick={closeMenu}
                  className="flex items-center gap-3 px-4 py-3 text-foreground hover:text-primary hover:bg-white/5 rounded-lg transition-all"
                >
                  Login
                </Link>
                <Link 
                  to="/register" 
                  onClick={closeMenu}
                  className="flex items-center gap-3 px-4 py-3 text-primary-foreground bg-primary hover:bg-primary/90 rounded-lg transition-all font-semibold"
                >
                  Register
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
